import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Breakdown, Loader, ui, type BreakdownRow, type FlowValues } from '../ui';
import type { ProductType } from '@goodbuild/rules/contracts';
import { CREATOR_RATE_BPS, PLATFORM_RATE_BPS } from '@goodbuild/rules/ledger';
import { formatMoney } from '@goodbuild/rules/format';
import {
  MODELLED_FEE_BPS,
  MODELLED_FEE_FIXED_MINOR,
  MODELLED_TAX_BPS,
  modelledSale,
} from '@goodbuild/rules/creator-earnings';
import { nearestPricePoint, PRICE_POINTS_MINOR } from '@goodbuild/rules/pricing';
import { suggestSlug } from '@goodbuild/rules/slug';
import { useApiMutation } from '../api';

/**
 * The parts a title-creation flow is built from, shared by the two that exist.
 *
 * There are two doors into the catalogue and there have to be. A studio submits
 * its own game at `/submit`; somebody on staff adds one at `/admin/games/new`,
 * which is the only way a studio that has never opened a portal gets its first
 * title (#126). The API already answers that duplication properly — both routes
 * validate with `titleCreateInput` and write through `createTitle`, and
 * `lib/admin/games.ts` still has no create of its own — and until #127 the
 * browser did not: one door was a flow with a Steam import, an address resolved
 * against the catalogue and a worked example of the split, and the other was
 * six inputs in a panel.
 *
 * Making the second one match meant either copying four hundred lines or
 * putting them here. This is here.
 *
 * ## What is shared, and what deliberately is not
 *
 * **Shared: everything that can be wrong.** Resolving an address against the
 * catalogue, reading a Steam page, showing what came back so somebody can see
 * they pasted the wrong game, the price ladder, and the arithmetic of a sale.
 * Every one of those is a place where two copies would drift and the drift
 * would be invisible — an admin-created title landing on a slug the studio flow
 * would have skipped, or a breakdown quoting a rate the ledger stopped using.
 *
 * **Not shared: the questions.** Each flow declares its own `FlowStep[]`, in
 * its own words. They ask for the same four things and they are not asking the
 * same person: `/submit` is addressed to somebody about to hand us their game
 * and says *you* and *your game*, and the admin form is addressed to somebody
 * typing on that studio's behalf, who needs to be told which studio a question
 * is about and that nothing here is the studio's own submission. A shared step
 * builder with an `audience` flag would have collapsed that into a ternary per
 * sentence, which is how copy stops being written and starts being configured.
 */

/** What `GET /v1/portal/steam` hands back. See `lib/steam/parse.ts`. */
export interface SteamDraft {
  appId: number;
  storeUrl: string;
  name: string;
  slug: string;
  type: ProductType;
  priceMinor: number | null;
  isFree: boolean;
  shortDescription: string;
  headerImage: string;
  developers: string[];
  publishers: string[];
  comingSoon: boolean;
}

/**
 * The address a title will live at, and what it cost to get there.
 *
 * Resolved against the catalogue while the name is being typed rather than at
 * the moment of sending, because "goodbuild.cc/g/tiny-city, if it is free" is a
 * hedge and the thing it hedges about is a lookup we can do.
 *
 * `name` is carried so a resolution cannot outlive the answer it was made for.
 * Somebody who types a name, waits for the address and then edits the name has
 * an address for a game that no longer has that title, and using it would put
 * the wrong slug on the row.
 */
export interface Address {
  /** The name this was resolved for. A resolution is only good for its name. */
  name: string;
  /** The address that is free, and the one the create is asked for. */
  slug: string;
  /** What the name alone asks for, when that one is already taken. */
  wanted: string | null;
  /**
   * Whether the catalogue actually answered.
   *
   * False covers two cases and the sentence under the field covers both: the
   * request failed, or twenty games share this name. In neither is `slug` a
   * promise, and the create says so if it turns out to be taken.
   */
  checked: boolean;
}

/**
 * How long a pause in typing counts as a name.
 *
 * Long enough that a name is not a request per keystroke, short enough that the
 * address is on screen before somebody has finished reading the question under
 * it. The step's own action resolves anyway on the way out, so a fast typist
 * loses nothing by outrunning this.
 */
const ADDRESS_SETTLE_MS = 400;

/** The price rungs, as a flow reads them. The ladder itself lives in core. */
export const PRICE_CARDS = PRICE_POINTS_MINOR.map((minor) => ({
  value: String(minor),
  label: formatMoney('EUR', minor),
}));

/*
 * There is no `useAvailability` here, and the first draft of this file had one.
 *
 * `./shared` already exports it, and its note said `submit.tsx` builds the same
 * resolver inline because it needs the raw lookup as well as the `check` prop —
 * true, and not a reason for a second copy: `useStoreAddress` below takes the
 * resolver as an argument, so the one in `./shared` serves both uses. Two
 * transports for "is this address free" is how one of them keeps working after
 * the endpoint moves and the other quietly answers *no objection* for ever.
 */

/**
 * The address this title will live at, resolved rather than asked for.
 *
 * **Nobody is asked where a game should live.** The question was `Where should
 * it live on the store?` over a prefixed field, pre-filled from the name — so
 * the ordinary answer was to read a proposal, agree with it and press Next. A
 * screen whose usual outcome is "yes, that one" is a screen that should not
 * exist.
 *
 * What the question was carrying is the collision: `product.slug` is unique
 * across the catalogue, and two studios called their games the same thing. That
 * is a lookup rather than a question, so this does the lookup — the obvious
 * address if it is free, and the next number up if it is not.
 *
 * **It runs while the name is being typed**, not at the moment of sending.
 * Section 06 is about showing the consequence of an answer *as it is given*,
 * and "if it is free" is precisely the part a lookup can settle.
 *
 * `resolve` is called from three places and short-circuits in two of them: the
 * debounce under the field, the name step's own action on the way out — which
 * is what guarantees the review screen has an answer even for somebody who
 * typed fast and pressed Enter — and the submit, which will not use a
 * resolution belonging to a different name.
 *
 * A failed check reads as no objection, exactly as the flow engine's own does.
 * The create checks the same thing inside the write, so this is a courtesy; a
 * network blip must not push somebody onto `tiny-city-2` while `tiny-city` is
 * sitting there free. The write is still the authority and can still refuse,
 * because a slug can be taken between this answer and that insert.
 */
export function useStoreAddress(
  checkAvailability: (kind: string, value: string) => Promise<string | null>,
): {
  address: Address | null;
  resolve: (name: string) => Promise<Address>;
  /**
   * Throw away what was resolved, so the next `resolve` asks again.
   *
   * For one caller and one status: the create answering 409 because the address
   * went between the lookup and the insert. `resolve` short-circuits on a name
   * it has already answered for, so without this, pressing the button again
   * would send the same taken slug and collide for ever — which is the opposite
   * of what the API's own sentence tells somebody to expect.
   */
  forget: () => void;
} {
  const [address, setAddress] = useState<Address | null>(null);

  /*
   * The same value as `address`, readable without waiting for a render.
   *
   * `resolve` is called from an effect, from a step's action and from the
   * submit handler, and each has to know what the previous one settled before
   * React has re-rendered anything. Reading it out of state would make the
   * action fire a second lookup for a name the debounce has already answered,
   * which is the request this whole arrangement exists to avoid.
   */
  const settled = useRef<Address | null>(null);

  const resolve = useCallback(
    async (name: string): Promise<Address> => {
      const wanted = suggestSlug(name);

      const known = settled.current;
      if (known && known.name === name && known.checked) return known;

      /*
       * The fallback, and it is the obvious address rather than a number.
       *
       * This is what is returned when the catalogue does not answer, and when
       * twenty games turn out to share a name — which is not a case worth more
       * code than this, and which the create refuses with a sentence of its
       * own. Either way `checked` stays false and the note under the field says
       * so rather than claiming an address we could not confirm.
       */
      let resolved: Address = { name, slug: wanted, wanted: null, checked: false };

      for (let attempt = 1; attempt <= 20; attempt += 1) {
        const candidate = attempt === 1 ? wanted : `${wanted}-${attempt}`;
        try {
          if ((await checkAvailability('product-slug', candidate)) === null) {
            resolved = {
              name,
              slug: candidate,
              wanted: candidate === wanted ? null : wanted,
              checked: true,
            };
            break;
          }
        } catch {
          break;
        }
      }

      settled.current = resolved;
      setAddress(resolved);
      return resolved;
    },
    [checkAvailability],
  );

  const forget = useCallback(() => {
    settled.current = null;
    setAddress(null);
  }, []);

  return { address, resolve, forget };
}

/**
 * What the address under the name means, and what happens to it.
 *
 * The `hint` on the name step prints the address itself, in the engine's own
 * mono treatment. This is the sentence beside it, and it exists because the
 * address alone answers only half the question: whether the one that was wanted
 * was free, and whether the one on screen is really this title's.
 *
 * **It is also where the lookup is driven from**, which is why a component that
 * renders one sentence has two effects in it. `preview` is called on every
 * render of the step, so this is the one place that knows the current answer
 * without the step having to push it anywhere.
 *
 * The resolver is read through a ref rather than named in the dependency list.
 * It is stable today, because `useApiMutation` is; depending on that from
 * inside a debounce is how the timer ends up cleared and restarted on every
 * render, and the request never fires at all. This is the failure this
 * arrangement has, so it is worth not being one refactor away from it.
 *
 * `held` says what happens to the address once the form is sent, and the two
 * callers mean different things by it: a studio's submission holds it through
 * curation, and an administrator's create holds it from the moment the draft
 * exists. Passed in rather than branched on here, because this component has no
 * business knowing which form it is inside.
 */
export function StoreAddress({
  name,
  address,
  resolve,
  held,
}: {
  name: string;
  address: Address | null;
  resolve: (name: string) => Promise<Address>;
  /** What happens to the address once this form is sent. One clause. */
  held: string;
}): React.ReactElement | null {
  const typed = name.trim();
  const wanted = suggestSlug(typed);

  const latest = useRef(resolve);
  useEffect(() => {
    latest.current = resolve;
  });

  useEffect(() => {
    if (wanted === '') return;
    const timer = setTimeout(() => void latest.current(typed), ADDRESS_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [typed, wanted]);

  // Nothing to say about a name with no letters or digits in it. `validate` on
  // the step already refuses that one, in words about the name rather than
  // about an address that could not be built from it.
  if (wanted === '') return null;

  // A resolution belongs to the name it was made for. Anything else is the
  // previous name's answer sitting under the current name's address.
  const settledFor = address && address.name === typed ? address : null;

  if (!settledFor) {
    return (
      <p className={ui.statLabel ?? ''}>
        <Loader size="sm" label="Checking whether it is free" />
      </p>
    );
  }

  if (!settledFor.checked) {
    return (
      <p className={ui.statLabel ?? ''}>
        We could not check the catalogue just now. This is the address if it is free, and the create
        tells you for certain.
      </p>
    );
  }

  return (
    <p className={ui.statLabel ?? ''}>
      {settledFor.wanted
        ? `goodbuild.cc/g/${settledFor.wanted} is already taken, so this is the next one free. ${held}`
        : `Free. ${held}`}
    </p>
  );
}

/**
 * Pull a Steam page into the answers, on the way out of the link question.
 *
 * Most games arriving here already have one, and everything the form asks for
 * except the video is already written on it. It fills the flow in and creates
 * nothing: every answer stays editable and the review screen is still what
 * sends anything, because a title on GOODBUILD is a different product from the
 * same game on Steam — a different price, different terms — and an import that
 * went straight through would have somebody agreeing to terms they never read.
 *
 * **There is no `Fill from Steam` button.** It sat beside Next with nothing to
 * say which of the two was the one to press, and pressing Next first carried an
 * empty name and an empty price into the next three questions. Next reads the
 * page now, and is dead until there is a link to read. See `FlowAction`.
 *
 * The type Steam reports is read and dropped. `parse.ts` refuses anything that
 * is not a game rather than guessing, so it is worth having as a check on the
 * link, and there is no question here for it to answer.
 *
 * **The admin form calls the same endpoint as the studio's** (#128).
 * `GET /v1/portal/steam` is behind `requireCaller` and deliberately not behind
 * `assertStudioAccess` — there is no studio yet when somebody is filling in a
 * first title — so an administrator is already an acceptable caller, and a
 * second endpoint under `/admin` would be a second place for the parse, the
 * host allowlist and the rate-limit budget to be got wrong.
 */
export function useSteamFill(
  onFound: (draft: SteamDraft | null) => void,
): (link: string) => Promise<FlowValues> {
  const call = useApiMutation();

  return useMemo(
    () =>
      async (link: string): Promise<FlowValues> => {
        onFound(null);

        // A GET with the link as a query parameter, so nothing about this reads
        // as a write. The endpoint reduces it to an app id before it fetches.
        const result = await call<{ draft: SteamDraft }>(
          `/admin/games/import/steam?link=${encodeURIComponent(link.trim())}`,
          { method: 'GET' },
        );

        onFound(result.draft);

        return {
          name: result.draft.name,
          /*
            The byline, from whoever Steam says made it (#144).

            Steam returns a list and this joins it, because a game made by two
            studios is credited to both on its own store page and there is no
            reason ours should pick one. Empty when Steam has no answer, which
            leaves the field on its `GOODBUILD` default rather than blanking it.
          */
          ...(result.draft.developers.length > 0
            ? { developerName: result.draft.developers.join(', ') }
            : {}),
          /*
            No address. Steam proposes one and it was written into the answers,
            where nothing read it — there is no address step for it to land on,
            so `submitValues` dropped it. Which is the right outcome for the
            wrong reason: Steam's slug is free on Steam, and whether it is free
            here is a question about our catalogue. `useStoreAddress` asks it,
            from the name Steam just gave us, on the very next screen.
          */
          // Steam quotes 1799 and the ladder has no such rung, so this lands on
          // the nearest one. Which rung it landed on is on screen at the price
          // question, and changing it is one click.
          priceMinor:
            result.draft.priceMinor === null
              ? ''
              : String(nearestPricePoint(result.draft.priceMinor)),
        };
      },
    [call, onFound],
  );
}

/**
 * What came back from Steam, under the field that fetched it.
 *
 * Evidence rather than content. None of this is imported — the art belongs in
 * our own storage before a store page depends on it — but a picture of the game
 * is the fastest way for somebody to see that they pasted the wrong link, which
 * is the failure this feature actually has.
 */
export function SteamEvidence({ draft }: { draft: SteamDraft | null }): React.ReactElement | null {
  if (!draft) return null;

  return (
    <div className={ui.point ?? ''}>
      {draft.headerImage ? (
        // `alt` is empty because the name is on the next line and the picture
        // says nothing the words do not.
        <img
          src={draft.headerImage}
          alt=""
          width="460"
          height="215"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      ) : null}

      <p className="gb-body">
        <strong>{draft.name}</strong>
        {draft.developers.length > 0 ? ` — ${draft.developers.join(', ')}` : ''}
      </p>
      {draft.shortDescription ? <p className="gb-body">{draft.shortDescription}</p> : null}

      <p className={ui.statLabel ?? ''}>
        {/*
          Why the price may still be unanswered. Three reasons and all of them
          leave the same blank, so this has to say which — an empty price with
          no explanation reads as an import that failed.
        */}
        {draft.isFree
          ? 'Free on Steam, so there is no price to copy — this store has a floor and cannot list at zero.'
          : draft.priceMinor === null
            ? 'Steam does not quote this one in euros, so the price is left to be chosen. We never convert a currency.'
            : `Priced at ${formatMoney('EUR', draft.priceMinor)} on Steam, so the nearest price here is ${formatMoney('EUR', nearestPricePoint(draft.priceMinor))}.`}
      </p>
      <p className={ui.statLabel ?? ''}>
        Filled in from{' '}
        <a href={draft.storeUrl} target="_blank" rel="noopener noreferrer">
          app {draft.appId}
        </a>
        . Nothing is created until the review at the end.
      </p>
    </div>
  );
}

/**
 * One sale, all the way down, at the price on screen.
 *
 * **The figures depend on things this screen already knows and would otherwise
 * be worked out by hand**: that the commission comes out of net rather than
 * list, that the tax is inside the price rather than on top of it, and that
 * processing is taken before anyone is paid. The most common way to get this
 * wrong flatters us — reading the creator's rate off the sticker price
 * overstates what they get.
 *
 * So it is arithmetic rather than a sentence, and it is the *real* arithmetic:
 * `modelledSale` runs the same `computeRevenueSplit` that resolves what an
 * order actually pays out when it clears. A worked example that disagreed by a
 * cent would be worse than none.
 *
 * **The buyer is modelled and the caption says so.** Section 11.1's worked
 * example — a Polish buyer at 23% VAT, processing at 3.5% plus 0.25 — because
 * that is the model the creator board already shows creators for the same
 * title, and the two must be looking at the same table.
 *
 * **There were three shares below the net and now there are two** (#144). The
 * middle row was the studio's, at 70% of the distributable amount under section
 * 5.3's Full Service tier, and `studioLabel` named it — `The studio` here and
 * `You` on the studio's own form. There is no studio and no tier:
 * `PLATFORM_RATE_BPS` is the whole of what is left after the commission.
 *
 * **The rate is `CREATOR_RATE_BPS` rather than an answer** (#129). It hung off
 * a rate question until that question was removed; it is on the price question
 * now, which is the only remaining answer that moves any of these figures.
 */
export function NetEarnings({ priceMinor }: { priceMinor: number }): React.ReactElement | null {
  const sale = useMemo(() => {
    if (!Number.isFinite(priceMinor) || priceMinor <= 0) return null;
    return modelledSale('EUR', priceMinor, CREATOR_RATE_BPS, PLATFORM_RATE_BPS);
  }, [priceMinor]);

  // Nothing to model until a rung is picked, which is the state this step opens
  // in. The rate is no longer an answer that can be missing (#129).
  if (!sale) return null;

  const money = (minor: number): string => formatMoney('EUR', minor);
  const share = (minor: number): string => `${Math.round((minor / sale.net) * 100)}% of the net`;

  const rows: readonly BreakdownRow[] = [
    { label: 'What the player pays', value: money(sale.pricePaid), total: true },
    {
      label: 'VAT',
      note: `${MODELLED_TAX_BPS / 100}%, inside the price rather than on top of it`,
      value: `−${money(sale.tax)}`,
    },
    {
      label: 'Payment processing',
      note: `${MODELLED_FEE_BPS / 100}% plus ${money(MODELLED_FEE_FIXED_MINOR)}`,
      value: `−${money(sale.paymentFees)}`,
    },
    { label: 'Net', value: money(sale.net), total: true },
    {
      label: 'The creator who sold it',
      note: `${share(sale.commission)}, taken first`,
      value: money(sale.commission),
    },
    {
      label: 'GOODBUILD',
      note: share(sale.platformGross),
      value: money(sale.platformGross),
    },
  ];

  return (
    <Breakdown
      rows={rows}
      caption="One sale to a Polish buyer, modelled. Tax is the buyer's and varies by country; the two shares below the net do not."
    />
  );
}
